package io.horion.cv.components

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.borderRadius
import com.varabyte.kobweb.compose.ui.modifiers.fontFamily
import com.varabyte.kobweb.compose.ui.modifiers.fontWeight
import com.varabyte.kobweb.compose.ui.modifiers.position
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.core.rememberPageContext
import com.varabyte.kobweb.silk.components.forms.Button
import com.varabyte.kobweb.silk.components.icons.fa.FaEye
import com.varabyte.kobweb.silk.components.icons.fa.IconSize
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.toModifier
import io.horion.cv.api.utils.TokenRepository
import io.horion.cv.styles.DownloadButtonCompactStyle
import io.horion.cv.styles.DownloadButtonDesktopStyle
import io.horion.cv.styles.DownloadButtonDesktopWrapperStyle
import io.horion.cv.styles.DownloadButtonMobileWrapperStyle
import io.horion.cv.styles.FormActionButtonStyle
import io.horion.cv.styles.ViewButtonStyle
import io.horion.cv.utils.Constants.INTER
import io.horion.cv.viewModel.CvViewModel
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.css.Position
import org.jetbrains.compose.web.css.px

private val FIELD_LABELS = mapOf(
    "lastName" to "Prénom(s)",
    "firstName" to "Nom(s) de famille",
    "bp" to "Emploi recherché",
    "email" to "Adresse e-mail",
    "phone" to "Numéro de téléphone",
    "city" to "Ville"
)

private val activeDownloadOwner = mutableStateOf<String?>(null)
private var downloadButtonCounter = 0

@Composable
fun DownloadCvButton(
    cvViewModel: CvViewModel,
    compact: Boolean = false,
    showIcon: Boolean = false,
    popupOpensDownward: Boolean = false,
) {
    val ctx = rememberPageContext()
    val scope = rememberCoroutineScope()

    val ownerId = remember {
        "download-cv-btn-${++downloadButtonCounter}"
    }

    val isOwner = activeDownloadOwner.value == ownerId

    var showAuthModal by remember {
        mutableStateOf(false)
    }

    var isAuthenticated by remember {
        mutableStateOf(false)
    }

    if (showAuthModal) {
        AuthRequiredPopup(
            onDismiss = {
                showAuthModal = false
            },
            onConfirm = {
                showAuthModal = false
                TokenRepository.redirectToAuth()
            }
        )
    }

    LaunchedEffect(
        cvViewModel.cvUiState.cvCreateSuccess,
        isOwner
    ) {
        if (
            isOwner &&
            cvViewModel.cvUiState.cvCreateSuccess
        ) {
            ctx.router.navigateTo("/payment")
        }
    }

    if (isOwner) {
        LoadingPopup(
            cvViewModel.cvUiState.loading
        )
    }

    Box(
        modifier = Modifier.position(Position.Relative)
    ) {
        if (
            isOwner &&
            cvViewModel.showValidationWarning
        ) {
            ValidationWarningPopup(
                missingLabels = cvViewModel.missingFieldIds
                    .mapNotNull { FIELD_LABELS[it] },
                openDownward = popupOpensDownward,
                onDismiss = {
                    cvViewModel.dismissValidationWarning()
                    activeDownloadOwner.value = null
                },
                onFieldClick = { index ->
                    focusMissingFieldFromPreview(
                        cvViewModel.missingFieldIds.getOrNull(index)
                    )
                }
            )
        }

        Box(
            modifier = DownloadButtonMobileWrapperStyle.toModifier()
        ) {
            Button(
                onClick = {
                    ctx.router.navigateTo("/cv_preview")
                },
                modifier = ViewButtonStyle
                    .toModifier()
                    .then(
                        if (compact) {
                            DownloadButtonCompactStyle.toModifier()
                        } else {
                            FormActionButtonStyle.toModifier()
                        }
                    )
                    .borderRadius(
                        if (compact) 6.px else 7.px
                    )
                    .fontFamily(INTER)
                    .fontWeight(FontWeight.Normal)
                    .styleModifier {
                        property("display", "flex")
                        property("align-items", "center")
                        property("justify-content", "center")
                        property("text-align", "center")
                        property("gap", "8px")
                        property("white-space", "nowrap")
                        property("box-sizing", "border-box")
                    }
            ) {
                if (compact) {
                    FaEye(
                        size = IconSize.SM
                    )
                } else {
                    SpanText("Aperçu")
                }
            }
        }

        Box(
            modifier = DownloadButtonDesktopWrapperStyle.toModifier()
        ) {
            Button(
                onClick = {
                    activeDownloadOwner.value = ownerId

                    if (cvViewModel.validateRequiredFields()) {
                        scope.launch {
                            isAuthenticated =
                                runCatching {
                                    TokenRepository.checkAuthentication()
                                }.getOrDefault(false)

                            if (isAuthenticated) {
                                cvViewModel.createCv()
                            } else {
                                showAuthModal = true
                            }
                        }
                    } else {
                        focusMissingFieldFromPreview(
                            cvViewModel.missingFieldIds.firstOrNull()
                        )
                    }
                },
                modifier = ViewButtonStyle
                    .toModifier()
                    .then(
                        if (compact) {
                            DownloadButtonCompactStyle.toModifier()
                        } else {
                            FormActionButtonStyle.toModifier()
                        }
                    )
                    .then(DownloadButtonDesktopStyle.toModifier())
                    .borderRadius(
                        if (compact) 6.px else 7.px
                    )
                    .fontFamily(INTER)
                    .fontWeight(FontWeight.Normal)
                    .styleModifier {
                        property("display", "flex")
                        property("align-items", "center")
                        property("justify-content", "center")
                        property("text-align", "center")
                        property("gap", "8px")
                        property("white-space", "nowrap")
                        property("box-sizing", "border-box")
                    }
            ) {
                SpanText(
                    text = "Télécharger"
                )
            }
        }
    }
}